import React from 'react'

import { cn } from '@/utils/shadcn/utils'

import {
  type SearchContainerProps,
  type SearchDropdownProps,
  type SearchResultItemProps,
  type SearchResultMessageProps,
  SearchResultMessageVariant,
  type SearchResultsListProps,
} from './types'

export const SearchDropdown = React.forwardRef<
  HTMLDivElement,
  SearchDropdownProps
>(function SearchDropdown({ className, children, ...props }, ref) {
  return (
    <div
      className={cn(
        'absolute left-0 right-0 z-50 mt-1 bg-background border border-stroke rounded-md shadow-lg w-full',
        className,
      )}
      role="presentation"
      aria-hidden="true"
      ref={ref}
      {...props}
    >
      {children}
    </div>
  )
})

export const SearchResultsList = React.forwardRef<
  HTMLUListElement,
  SearchResultsListProps
>(function SearchResultsList({ className, children, ...props }, ref) {
  return (
    <ul
      className={cn(
        'divide-y bg-background divide-secondary-background max-h-60 overflow-auto w-full rounded-lg',
        className,
      )}
      tabIndex={-1}
      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Bulk suppression as part of biome migration
      role="listbox"
      aria-label="Search results"
      ref={ref}
      {...props}
    >
      {children}
    </ul>
  )
})

export const SearchResultItem = React.memo(function SearchResultItem({
  className,
  children,
  isFocused,
  ...props
}: SearchResultItemProps) {
  return (
    <li
      className={cn(
        'bg-background flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-secondary-background w-full',
        isFocused && 'bg-secondary-background',
        className,
      )}
      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Bulk suppression as part of biome migration
      role="option"
      tabIndex={0}
      aria-selected={isFocused}
      {...props}
    >
      {children}
    </li>
  )
})

export const SearchContainer = React.memo(function SearchContainer({
  className,
  children,
  ...props
}: SearchContainerProps) {
  return (
    <div className={cn('relative w-full min-w-[110px]', className)} {...props}>
      {children}
    </div>
  )
})

export const SearchResultMessage = React.memo(function SearchResultMessage({
  children,
  variant = SearchResultMessageVariant.EMPTY,
  className,
  ...props
}: SearchResultMessageProps) {
  const base = 'px-4 py-3 text-sm'
  const color =
    variant === 'loading' || variant === 'empty'
      ? 'text-moment-blue-grey-400 dark:text-moment-blue-grey-500'
      : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
        variant === 'error'
        ? 'text-moment-red-500 dark:text-moment-red-400'
        : ''
  return (
    <li className={cn(base, color, className)} {...props}>
      {children}
    </li>
  )
})
